'use client';

import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {OlympusRadii} from '@olympus-frontend/tenant-config';
import type {ReactNode} from 'react';

export interface ResponsiveModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  maxWidth?: number;
  radius?: keyof OlympusRadii;
}

// The sheet slides in slower than the MUI default (225ms) so the motion reads
// as deliberate rather than snappy. `easeOut` is already Slide's own default
// enter easing — set explicitly so the intent isn't silently lost if that
// default ever changes.
const SHEET_ENTER_DURATION = 400;
const SHEET_EXIT_DURATION = 300;

export function ResponsiveModal({open, onClose, children, labelledBy, maxWidth = 720, radius}: ResponsiveModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    const sheetRadius = `${theme.radii['2xl']}px`;
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        transitionDuration={{enter: SHEET_ENTER_DURATION, exit: SHEET_EXIT_DURATION}}
        // `aria-labelledby` goes on the paper, not the Drawer root: the root is
        // role="presentation" (inert for naming) while the paper is the
        // role="dialog" element that needs the accessible name.
        slotProps={{
          paper: {
            'aria-labelledby': labelledBy,
            // Caps the sheet like the desktop dialog's own viewport cap so a
            // tall form scrolls its Body instead of pushing the header (and,
            // on short screens, the whole sheet) off the top of the viewport.
            sx: {borderStartStartRadius: sheetRadius, borderStartEndRadius: sheetRadius, maxHeight: '90dvh'},
          },
          // Drawer passes appear=false on its first render, and this sheet always mounts already open.
          transition: {appear: true, easing: {enter: theme.transitions.easing.easeOut, exit: theme.transitions.easing.sharp}},
        }}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={labelledBy}
      slotProps={{
        paper: {
          sx: {
            width: '100%',
            maxWidth,
            borderRadius: radius ? `${theme.radii[radius]}px` : undefined,
          },
        },
      }}
    >
      {children}
    </Dialog>
  );
}

ResponsiveModal.displayName = 'ResponsiveModal';
